<script setup>
//#region import
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';
import numeral from 'numeral';
import {
  isValid,
  format as formatDateFns,
  intervalToDuration,
  formatDuration,
} from 'date-fns';
import {
  isEmpty,
  cloneDeep,
  isNull,
  debounce,
  uniqBy,
  concat,
  remove,
  assign,
  forEach,
  get,
  floor,
} from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { useTypedStore } from '@/store';
import eventBus from '@/setup/eventBus';
import { locales } from '@/helpers/date-locales';
import Vnodes from '@/components/VNodes.vue';
import SortableElTableNew from '@/components/SortableElTableNew.vue';
import { useCopy } from '@/composables/useCopy';
import LazyLoadingTable from './uiCustomComponent/LazyLoadingTable.vue';
import RenderHeaderNew from './UiTableHelper/RenderHeaderNew.vue';
//#endregion

//#region props
const props = defineProps({
  emptyIconName: {
    type: String,
    default: () => 'folder-open',
  },
  randomValue: {
    type: String,
    default: () => '',
  },
  currency: {
    type: String,
    default: () => '',
  },
  resizable: {
    type: Boolean,
    default: () => true,
  },
  switchDefaultCheck: {
    type: Boolean,
    default: () => true,
  },
  columnCheckboxWidth: {
    type: Number,
    default: () => 34,
  },
  hiddenCheckBox: {
    type: [Boolean, String],
    default: () => false,
  },
  // скрывает чекбокс для выбора всех полей
  showCheckAll: {
    type: Boolean,
    default: () => true,
  },
  selectRow: {
    type: Boolean,
    default: () => false,
  },
  lazyLoading: {
    type: Boolean,
    default: () => null,
  },
  lazyLoadingDelay: {
    type: Number,
    default: () => 3000,
  },
  emptyText: {
    type: String,
    default: () => '',
  },
  checkedRow: {
    type: Boolean,
    default: () => false,
  },
  checkedRowId: {
    type: String,
    default: () => 'id',
  },
  fields: {
    type: Array,
    default: () => [],
  },
  data: {
    type: Array,
    default: () => [],
  },
  showTotal: {
    type: Boolean,
    default: () => false,
  },
  inPopup: {
    type: Boolean,
    default: () => false,
  },
  total: {
    type: Object,
    default: () => ({}),
  },
  customTotalRow: {
    type: Array,
    default: () => [],
  },
  sort: {
    type: Object,
    default: () => ({}),
  },
  i18nPath: {
    type: String,
    default: () => '',
  },
  minTableHeight: {
    type: Number,
    default: () => 400,
  },
  maxTableHeight: {
    type: Number,
    default: () => null,
  },
  padding: {
    type: Number,
    default: () => 40,
  },
  rowsCount: {
    type: Number,
    default: () => 0,
  },
  isStatic: {
    type: Boolean,
    default: () => false,
  },
  disableSort: {
    type: Boolean,
    default: () => false,
  },
  externalHeight: {
    type: [Number, String],
    default: () => '',
  },
  showHeader: {
    type: Boolean,
    default: () => true,
  },
  selectType: {
    type: String,
    default: () => 'checkbox',
  },
  actions: {
    type: [Object, Array],
    default: () => null,
  },
  rowClick: {
    type: Function,
    default: () => {},
  },
  rowSelectable: {
    type: Boolean,
    default: () => false,
  },
  colFilters: {
    type: Object,
    default: () => ({}),
  },
  spanMethod: {
    type: Function,
    default: () => '',
  },
  customContext: {
    type: Boolean,
    default: () => false,
  },
  computedRowClass: {
    type: Function,
    default: () => '',
  },
  customSorting: {
    type: Boolean,
    default: () => false,
  },
  headerRowClass: {
    type: String,
    default: () => '',
  },
  offsetColumn: {
    type: Number,
    default: () => 0,
  },
  debounceDelay: {
    type: Number,
    default: () => 300,
  },
  defaultSelectedRows: {
    type: Array,
    default: () => [],
  },
  resizeFix: {
    type: Boolean,
    default: () => false,
  },
  fillParentHeight: {
    type: Boolean,
    default: () => false,
  },
});
//#endregion

//#region emit
const emit = defineEmits([
  'row-click',
  'current-change',
  'expand-change',
  'sort',
  'change-rows-count',
  'context',
  'checkedRows',
  'headerDragend',
  'filter',
  'init',
  'update',
]);
//#endregion

//#region data
const store = useTypedStore();
const $i18n = useI18n();
const { t, te } = useI18n();
const { copy } = useCopy();

const isCopyEnabled = (field, row) => {
  return typeof field.copyValue === 'function'
    ? field.copyValue(row)
    : (!!field.copyValue && row[field.name]);
};

const tableHeight = ref(0);
const datatableRef = ref(null);

const sortProp = ref('');
const sortOrder = ref('');

const columnFilters = ref({});
const lastOpenFilter = ref('');
const isFiltersInit = ref(false);
const computedRowsCount = ref(0);

const lastSortProp = ref(props.sort.prop ?? '');
const lastSortOrder = ref(props.sort.order ?? '');
const checkRows = ref([...props.defaultSelectedRows]);
const checkAll = ref(false);

const customFilterColumn = [
  'players_avg_lifetime_seconds',
  'players_median_lifetime_seconds',
  'players_avg_fd_since_registration_seconds',
  'players_median_fd_since_registration_seconds',
];

const customFilterColumnCreated = ['created_at', 'review_requested_at'];

const isLazyLoading = ref(false);
const lazyLoadingTable = ref(props.lazyLoading);

const checkApplyFilter = ref(false);
const activeFilter = ref('');
const activeColumnFilters = ref({});
const debouncedTableHeight = ref(null);
//#endregion

//#region computed
const countries = computed(() => store.getters['misc/countries']);
const groups = computed(() => store.getters['misc/groups']);
const currencies = computed(() => store.getters['misc/currencies']);


const computedHeight = computed(() => {
  return heightProp.value
    ? heightProp.value
    : undefined;
});

const heightProp = computed(() => {
  return props.maxTableHeight ? props.maxTableHeight
    : props.isStatic
      ? props.externalHeight || undefined
      : tableHeight.value || undefined;
});

const computedEmptyText = computed(() => {
  if (props.emptyText === '') {
    return t('ui.table.no_data');
  }
  return props.emptyText;
});

const getDefaultSort = computed(() => {
  return {
    prop: sortProp.value,
    order: `${sortOrder.value}ending`,
  };
});

const tooltipOptions = computed(() => {
  return {
    showAfter: 300,
    enterable: false,
  };
});
//#endregion

//#region watchers
watch(() => props.defaultSelectedRows, () => {
  checkRows.value = cloneDeep(props.defaultSelectedRows);
});

watch(lastOpenFilter, (newValue) => {
  if (newValue === '') {
    Array.from(document.querySelectorAll('.cell.hover-once')).map((e) => e.classList.remove('hover-once'));
    if (!checkApplyFilter.value) {
      columnFilters.value[activeFilter.value] = activeColumnFilters.value[activeFilter.value];
      columnFilters.value[activeFilter.value].visible = false;
    }
  } else {
    activeFilter.value = newValue;
    activeColumnFilters.value = cloneDeep(columnFilters.value);
  }
});

watch(() => props.data, (newValue) => {
  lazyLoadingTable.value = props.lazyLoading;
  checkSelect(newValue);
  /*
   * Чтобы повторно не вызывать lazy loader
   * */
  if (!isNull(lazyLoadingTable.value)) {
    lazyLoadingTable.value = null;
    setTimeout(() => {
      isLazyLoading.value = false;
      if (datatableRef.value) {
        nextTick(() => {
          datatableRef.value.doLayout();
          emit('update');
        });
      }
    }, 400);
  }
}, { deep: true } );

watch(() => props.sort, (newValue) => {
  if (props.customSorting) {
    sortProp.value = newValue.prop;
    sortOrder.value = newValue.order;
  } else {
    sortProp.value = newValue.prop;
    sortOrder.value = newValue.order;
    datatableRef.value.sort(sortProp.value, `${sortOrder.value}ending`);
    datatableRef.value.doLayout();
    nextTick(() => {
      datatableRef.value.sort(sortProp.value, `${sortOrder.value}ending`);
      datatableRef.value.doLayout();
    });
  }
}, { deep: true });

watch(() => props.fields, () => {
  isFiltersInit.value = false;
  props.fields.forEach((field) => {
    if (
      field.filterable
      && (isEmpty(columnFilters.value[field.name])
        || !columnFilters.value[field.name].op)
    ) {
      columnFilters.value[field.name] = {
        visible: false,
        op: '',
        value: '',
        valueRange: ['', ''],
        isExclude: false,
        valueSec: '',
        dateFilter: '',
        dateRange: null,
      };
    }
  });
  isFiltersInit.value = true;
}, { deep: true, immediate: true });

watch(() => props.colFilters, (newValue) => {
  setColumnFilters(newValue);
}, { deep: true });
//#endregion

//#region methods
const getRowClass = ({row}) => {
  return `ui-table__table-row ${props.computedRowClass(row)}`;
};

const getCurrencyLabel = (code = props.currency) => {
  if (!code) {
    return '$';
  }

  const currency = currencies.value.find(((el) => el.code === code));
  return currency?.symbol || `${code} `;
};

const changeExclude = (col, value) => {
  columnFilters.value[col].isExclude = value;
};

const handlerFilter = (filter, action) => {
  emit('filter', filter, action);
};

const handlerRadioBtn = (col, value) => {
  columnFilters.value[col].op = value;
  if (value === '=') columnFilters.value[col].dateRange = null;
  if (Array.isArray(columnFilters.value[col].dateRange)) {
    columnFilters.value[col].dateRange = null;
  }
};

const handlerDatepickerRange = (col, date) => {
  columnFilters.value[col].dateRange = date;
};

const handlerRangeValue = (type, col, value) => {
  if (type === 'dataRange[0]') {
    columnFilters.value[col].valueRange[0] = value;
  } else {
    columnFilters.value[col].valueRange[1] = value;
  }
};

const handlerColumnFilterInput = (type, col, value) => {
  if (type === 'num') {
    columnFilters.value[col].value = value;
  } else if (type === 'date') {
    columnFilters.value[col].valueSec = value;
    setTimeMoment(col);
  } else if (type === 'select') {
    columnFilters.value[col].dateFilter = value;
    setTimeMoment(col);
  }
};

const headerDragend = (columnWidth, startLeft_startColumnLeft, column, event) => {
  emit('headerDragend', columnWidth, startLeft_startColumnLeft, column, event);
};

const getLabel = (field) => {
  if (props.i18nPath) {
    if (field.label) return t(`${props.i18nPath}.fields.${field.label}.title`);

    return te(`${props.i18nPath}.fields.${field.name}.title`) ? t(`${props.i18nPath}.fields.${field.name}.title`) : '';
  }
  return field.title;
};

const hiddenCheckFoo = (row) => {
  if (props.hiddenCheckBox !== false) {
    let hide = true;
    switch (props.hiddenCheckBox) {
      case 'chargeback': {
        if (row.chargebacks_count !== 0) {
          hide = false;
        } else {
          hide = true;
        }
        break;
      }
      default: {
        hide = true;
        break;
      }
    }
    return hide;
  }
  return true;
};

const changeRowCheck = (row) => {
  if (props.selectType === 'checkbox') {
    if (row.disabled) {
      return;
    }

    if (checkRows.value.indexOf(row[props.checkedRowId]) === -1) {
      checkRows.value.push(row[props.checkedRowId]);
    } else {
      checkRows.value = checkRows.value.filter((item) => item !== row[props.checkedRowId]);
    }
  }

  if (props.selectType === 'radio') {
    if (checkRows.value.indexOf(row[props.checkedRowId]) === -1) {
      checkRows.value = [row[props.checkedRowId]];
    } else {
      checkRows.value = [];
    }
  }

  emit('checkedRows', row[props.checkedRowId], checkRows.value, row);

  checkSelect(props.data);
};

const checkSelect = (val) => {
  switch (props.hiddenCheckBox) {
    case 'chargeback': {
      checkAll.value = props.data.filter((item) => item.chargebacks_count === 0).length !== 0;
      const data = val.filter((item) => item.chargebacks_count === 0);
      if (data.length === 0) {
        checkAll.value = false;
      }
      data.forEach((e) => {
        if (checkRows.value.indexOf(e[props.checkedRowId]) === -1) {
          checkAll.value = false;
        }
      });
      break;
    }
    default: {
      checkAll.value = props.data.length !== 0;
      if (val.length === 0) {
        checkAll.value = false;
      }
      val.forEach((e) => {
        if (checkRows.value.indexOf(e[props.checkedRowId]) === -1) {
          checkAll.value = false;
        }
      });
      break;
    }
  }
};

const handleCheckAllChange = (val) => {
  checkRows.value = val ? checkAllFoo() : unCheckAllFoo();
  changeRow(val ? 'all' : 'alldel');
  checkAll.value = !checkAll.value;
};

const checkAllFoo = () => {
  const arrchall = [];

  props.data.forEach((e) => {
    switch (props.hiddenCheckBox) {
      case 'chargeback': {
        if (e.chargebacks_count === 0) {
          arrchall.push(e[props.checkedRowId]);
        }
        break;
      }
      default: {
        if (!e.disabled) {
          arrchall.push(e[props.checkedRowId]);
        }
        break;
      }
    }
  });
  return uniqBy(concat(checkRows.value, arrchall));
};

const unCheckAllFoo = () => {
  const arrchall = [];
  props.data.forEach((e) => {
    if (!e.disabled) {
      arrchall.push(e[props.checkedRowId]);
    }
  });
  return remove(
    checkRows.value,
    (arr) => arrchall.indexOf(arr) === -1,
  );
};

const changeRow = (val) => {
  if (val === 'all' || val === 'alldel') {
    emit('checkedRows', val, checkRows.value);
  } else {
    checkSelect(props.data);
    emit('checkedRows', val, checkRows.value);
  }
};

const contextMenuHandler = (row, column, event) => {
  if (props.customContext) {
    event.preventDefault();
    emit('context', row, event, column);
  }
};

const setColumnFilters = (filters) => {
  isFiltersInit.value = false;
  columnFilters.value = {};
  props.fields.forEach((field) => {
    if (field.filterable) {
      columnFilters.value[field.name] = {
        visible: false,
        op: '',
        value: '',
        valueRange: ['', ''],
        isExclude: false,
        valueSec: '',
        dateFilter: '',
        dateRange: null,
      };
    }
  });

  if (filters) {
    columnFilters.value = assign({}, columnFilters.value, filters);
  }
  forEach(columnFilters.value, (filter) => (filter.visible = false));
  isFiltersInit.value = true;
};

const computeTableHeight = () => {
  let th = window.innerHeight - datatableRef.value.$el.offsetTop;
  if (!props.showTotal) {
    th -= props.padding;
  }
  if (th !== tableHeight.value) {
    tableHeight.value = th < props.minTableHeight ? props.minTableHeight : th;
  }
};

// const computeRowsCount = () => {
//   const theader = datatableRef.value.$el.querySelector('.el-table__header-wrapper');
//   const tbody = datatableRef.value.$el.querySelector('.el-table__body-wrapper');
//   const ttotal = datatableRef.value.$el.querySelector('.el-table__footer-wrapper');
//   const trow = tbody.querySelector('.el-table__row td');
//   let bodyHeight = tbody.clientHeight;
//
//   if (props.showTotal && ttotal.offsetHeight === 0) {
//     bodyHeight -= 54;
//   }
//   if (this.showHeader && theader.offsetHeight === 0) {
//     bodyHeight -= 36;
//   }
//   if (tbody.scrollLeftMax > 0) {
//     bodyHeight -= 30;
//   }
//   const rows = Math.floor((bodyHeight / (trow ? trow.offsetHeight : 32)) - this.offsetColumn);
//   if (rows > 10 && rows !== this.rowsCount) {
//     this.computedRowsCount = rows;
//     emit('change-rows-count', rows);
//   }
// };

const handleSort = ({ prop, order }, id) => {
  if (props.customSorting) {
    if (lastSortProp.value === prop) {
      sortProp.value = prop;
      sortOrder.value = order;
      emit('sort', {
        prop: sortProp.value,
        order: sortOrder.value,
      });
      // remove 'asc' 'desc' classes
      const e = document.getElementsByClassName('el-table__header')[0];
      const removeClass = e.getElementsByTagName('th');
      for (let i = 0; i < removeClass.length; i += 1) {
        removeClass[i].classList.remove('asc', 'desc');
      }
      lastSortProp.value = prop;
      lastSortOrder.value = order;
    } else {
      sortProp.value = prop;
      sortOrder.value = lastSortOrder.value;
      emit('sort', {
        prop: sortProp.value,
        order: lastSortOrder.value,
      });
      // remove 'asc' 'desc' classes
      const e = document.getElementsByClassName('el-table__header')[0];
      const removeClass = e.getElementsByTagName('th');
      for (let i = 0; i < removeClass.length; i += 1) {
        removeClass[i].classList.remove('asc', 'desc');
      }
      lastSortProp.value = prop;
    }
    // add class
    const addClass = document.getElementsByClassName(id)[0];
    if (addClass.classList) {
      addClass.classList.add(sortOrder.value);
    }
    datatableRef.value.clearSort();
  } else {
    if (lastOpenFilter.value) {
      columnFilters.value[lastOpenFilter.value].visible = false;
      lastOpenFilter.value = '';
    }
    if (prop && order) {
      if (
        prop !== sortProp.value
        || order.substring(0, order.length - 6) !== sortOrder.value
      ) {
        sortProp.value = prop;
        sortOrder.value = order.substring(0, order.length - 6);
      } else {
        return;
      }
    } else {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
      datatableRef.value.sort(sortProp.value, `${sortOrder.value}ending`);
      datatableRef.value.doLayout();
      nextTick(() => {
        datatableRef.value.sort(sortProp.value, `${sortOrder.value}ending`);
        datatableRef.value.doLayout();
      });
    }
    emit('sort', {
      prop: sortProp.value,
      order: sortOrder.value,
    });
  }
};
const getSummaryRow = (param) => {
  if (props.customTotalRow && props.customTotalRow.length > 0) {
    return props.customTotalRow;
  }
  const { columns } = param;
  const sums = [];
  columns.forEach((column, index) => {
    switch (index) {
      case (props.checkedRow ? 1 : 0): {
        sums[index] = t('ui.table.total');
        break;
      }
      default: {
        sums[index] = '';
        const field = props.fields.find((el) => el.name === column.property);
        if (field) {
          sums[index] = field.format
            ? formatValue(
              props.total[field.name],
              field.format,
              field.formatOptions,
            )
            : props.total[field.name];
        }
        break;
      }
    }
  });
  return sums;
};
const setTimeMoment = (field) => {
  switch (columnFilters.value[field].dateFilter) {
    case 'minutes':
      columnFilters.value[field].value = +columnFilters.value[field].valueSec * 60;
      break;
    case 'hours':
      columnFilters.value[field].value = +columnFilters.value[field].valueSec * 3600;
      break;
    case 'days':
      columnFilters.value[field].value = +columnFilters.value[field].valueSec * 86400;
      break;
    case 'weeks':
      columnFilters.value[field].value = +columnFilters.value[field].valueSec * 86400 * 7;
      break;
    default:
      columnFilters.value[field].value = +columnFilters.value[field].valueSec;
  }
};
const formatValue = (value, format, options, row, type, field) => {
  if (type === 'report_table' && value === null) {
    return '—';
  }
  if (!value && value !== 0) {
    return '';
  }
  switch (format) {
    case 'device':
      return 'hello';
    case 'date': {
      return isValid(new Date(value))
        ? formatDateFns(value, !isEmpty(options) && options.out ? options.out : 'dd.MM.yyyy HH:mm', { locale: locales[$i18n.locale.value] })
        : value;
    }
    case 'format-date': {
      return isValid(new Date(value)) ? formatDateFns(value, 'dd MMM yyyy', { locale: locales[$i18n.locale.value] }) : '-';
    }
    case 'humanizeDuration': {
      if (value === 0) {
        return 0;
      }
      const duration = intervalToDuration({
        start: new Date(0),
        end: new Date(value * 1000),
      });
      return formatDuration(duration, { locale: locales[$i18n.locale.value] });
    }
    case 'date-time': {
      return isValid(new Date(value)) ? formatDateFns(value, 'dd MMM yyyy HH:mm', { locale: locales[$i18n.locale.value] }) : '-';
    }
    case 'number':
      return `${parseFloat(value).toLocaleString('en-US')}`;
    case 'percent':
      return `${parseFloat(value).toLocaleString('en-US')}%`;
    case 'odd':
      return `${parseFloat(parseFloat(value).toFixed(4)).toLocaleString('en-US')}`;
    case 'currency':
      return `${getCurrencyLabel(row?.currency_code)}${value < 0 ? '-' : ''}${Math.abs(floor(value / 100, 0)).toLocaleString('en-US')}`;
    case 'formatMoney':
      return `${getCurrencyLabel(get(row, [field?.currencyKey || 'currency_code']))}${value < 0 ? '-' : ''}${numeral(Math.abs(value)).format('0,0.00')}`;
    case '-formatMoney':
      return `${getCurrencyLabel(row?.currency_code)}-${numeral(Math.abs(value)).format('0,0.00')}`;
    case 'formatOriginalMoney':
      return `${getCurrencyLabel(row?.original_currency_code)}${value < 0 ? '-' : ''}${numeral(Math.abs(value)).format('0,0.00')}`;
    case 'ncurrency':
      return `${getCurrencyLabel(row?.currency_code)}${value < 0 ? '-' : ''}${Math.abs(floor(value, 0)).toLocaleString('en-US')}`;
    case '-ncurrency':
      return `${getCurrencyLabel(row?.currency_code)}${value === 0 ? '' : '-'}${Math.abs(floor(value, 0)).toLocaleString('en-US')}`;
    case 'country': {
      if (countries.value.length) {
        const c = countries.value.filter((country) => country.code === value);
        return c ? c[0].name : value;
      }
      return value;
    }
    case 'group': {
      const g = groups.value.filter((group) => group.id === value);
      return g ? g[0].name : value;
    }
    case 'eventType': {
      if (value === 1) {
        return 'Single';
      } if (value === 0) {
        return '';
      }
      return `Express-${value}`;
    }
    case 'event': {
      if (!Array.isArray(value)) return value;
      if (value.length === 1) {
        return value[0];
      } if (value.length > 1) {
        return `Express(${row.events_count}/${row.coefficient})`;
      }
      return value;
    }
    default:
      return value;
  }
};

const handleExpand = (row, expRows) => {
  emit('expand-change', row, expRows);
};

const handleCurrentRowChange = (current) => {
  emit('current-change', current);
};

const handleCurrentRowClick = (row, column, e) => {
  props.rowClick(row);
  emit('row-click', row);
  if (props.selectRow) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (props.switchDefaultCheck) changeRowCheck(row);
  }
};

const clearCheck = () => {
  checkRows.value = [];
  checkAll.value = false;
};

const setUiTableRef = (el) => {
  datatableRef.value = el;
};

function initDebouncedHeight() {
  debouncedTableHeight.value = debounce(computeTableHeight, props.debounceDelay);
}
function setCurrentRow(row) {
  datatableRef.value?.setCurrentRow(row);
}
//#endregion

//#region hooks
onMounted(() => {
  if (!props.isStatic) {
    initDebouncedHeight();
    debouncedTableHeight.value();
    window.addEventListener('resize', debouncedTableHeight.value);

    eventBus.$on('connection-enabled', debouncedTableHeight.value);
    // const stopWatch = watch(tableHeight, (newVal, oldVal) => {
    //   if (newVal !== oldVal) computeRowsCount();
    // });
  }
  emit('init');
});

onBeforeUnmount(() => {
  if (!props.isStatic) {
    window.removeEventListener('resize', debouncedTableHeight.value);
  }
  debouncedTableHeight.value?.cancel();
  eventBus.$off('connection-enabled', debouncedTableHeight.value);
  eventBus.$off('clear-check', clearCheck);
});

if (props.sort.prop || props.disableSort) {
  sortProp.value = '';
} else {
  sortProp.value = props.fields[0] ? props.fields[0].name : '';
}

sortOrder.value = props.sort.order || props.disableSort ? '' : 'asc';

document.addEventListener('click', (e) => {
  if (lastOpenFilter.value) {
    let targetEl = e.target;
    do {
      if (targetEl.classList
        && (
          targetEl.classList.contains('el-popover')
          || targetEl.classList.contains('custom-header__datepicker-menu')
        )
      ) {
        return;
      }
      targetEl = targetEl.parentNode;
    } while (targetEl);
    columnFilters.value[lastOpenFilter.value].visible = false;
    lastOpenFilter.value = '';
  }
});

if (!isEmpty(props.colFilters)) {
  columnFilters.value = cloneDeep(props.colFilters);
}
if (!isNull(props.lazyLoading) && props.lazyLoading !== false) {
  isLazyLoading.value = true;
}

eventBus.$on('clear-check', clearCheck);
//#endregion

defineExpose({
  clearCheck,
  changeRow,
  setCurrentRow,
  changeRowCheck,
  setColumnFilters,
});
</script>

<template>
  <div
    :class="[
      'ui-table',
      {
        // 'ui-table_data': data.length !== 0,
        // 'ui-table_fill-parent': fillParentHeight,
      }
    ]"
    :style="{ paddingBottom: !showTotal ? `${padding}px` : 'none' }"
  >
    <Transition name="fade">
      <SortableElTableNew handle=".drag">
        <template v-slot="{ setRef }">
          <ElTable
            v-show="!isLazyLoading"
            :ref="(el) => { setRef(el); setUiTableRef(el) }"
            :data="data"
            :span-method="spanMethod"
            :default-sort="getDefaultSort"
            :height="computedHeight"
            :max-height="heightProp"
            :highlight-current-row="rowSelectable"
            :row-class-name="getRowClass"
            cell-class-name="ui-table__table-cell"
            :header-row-class-name="headerRowClass"
            header-cell-class-name="ui-table__table-header-cell"
            :show-header="showHeader"
            :show-summary="showTotal"
            :summary-method="getSummaryRow"
            :class="[
              'ui-table__table',
              {
                'ui-table__table_border': showTotal,
                'ui-table__table_resize-border-fix': resizeFix
              }
            ]"
            :tooltip-options="tooltipOptions"
            border
            @current-change="handleCurrentRowChange"
            @expand-change="handleExpand"
            @row-click="handleCurrentRowClick"
            @row-contextmenu="contextMenuHandler"
            @sort-change="handleSort"
            @header-dragend="headerDragend"
          >
            <template v-if="checkedRow">
              <ElTableColumn
                :width="columnCheckboxWidth"
                class-name="ui-table__checkbox-col t:flex t:items-center t:justify-center"
              >
                <template v-slot:header="scope">
                  <div
                    v-if="showCheckAll"
                    :class="[
                      'custom-header',
                      {sortOrder: customSorting && scope.column.property === sortProp}
                    ]"
                  >
                    <UiCheckbox
                      :model-value="checkAll"
                      element-class="ui-table__checkbox"
                      @change="handleCheckAllChange"
                    />
                  </div>
                </template>
                <template v-slot="scope">
                  <UiCheckboxGroup
                    v-if="selectType === 'checkbox' && hiddenCheckFoo(scope.row)"
                    v-model="checkRows"
                    :disabled="scope.row.disabled"
                  >
                    <template v-slot:checkbox>
                      <UiCheckbox
                        :value="scope.row[checkedRowId]"
                        @change="changeRow(scope.row[checkedRowId])"
                      />
                    </template>
                  </UiCheckboxGroup>
                  <UiRadioGroup
                    v-if="selectType === 'radio' && hiddenCheckFoo(scope.row)"
                    v-model="checkRows[0]"
                  >
                    <template v-slot:buttons>
                      <UiRadioButton
                        :value="scope.row[checkedRowId]"
                        @change="changeRow(scope.row[checkedRowId])"
                      />
                    </template>
                  </UiRadioGroup>
                </template>
              </ElTableColumn>
            </template>
            <template v-slot:empty>
              <div class="ui-table__table-empty">
                <UiIcon
                  :size="32"
                  substyle="fal"
                  :name="emptyIconName"
                  color="inherit"
                  class="t:mb-8"
                />
                <div class="ui-table__table-empty-text">
                  {{ computedEmptyText }}
                </div>
              </div>
            </template>
            <slot
              :format-value="formatValue"
              name="columns"
            >
              <ElTableColumn
                v-for="(field) in $_.filter(fields, field => $_.isUndefined(field.show) ? true : field.show)"
                :key="field.order + field.name + field.forceUpdateKey || ''"
                :column-key="field.name"
                :fixed="field.fixed || false"
                :align="field.align || 'left'"
                :formatter="field.formatter"
                :header-align="field.headerAlign || 'left'"
                :label="getLabel(field)"
                :min-width="field.minWidth || 'auto'"
                :prop="field.name"
                :show-overflow-tooltip="!field.rowTooltip"
                :sortable="disableSort || customSorting ? false : field.sortable || false"
                :class-name="field.className"
                :type="field.checkbox || ''"
                :width="field.width || 'auto'"
                :resizable="field.resizable"
              >
                <template v-slot:header="scope">
                  <RenderHeaderNew
                    :column="scope.column"
                    :fields="fields"
                    :custom-sorting="customSorting"
                    :currency="currency"
                    :sort-prop="sortProp"
                    :sort-order="sortOrder"
                    :last-open-filter="lastOpenFilter"
                    :column-filters="columnFilters"
                    :is-filters-init="isFiltersInit"
                    :custom-filter-column="customFilterColumn"
                    :custom-filter-column-created="customFilterColumnCreated"
                    :check-apply-filter="checkApplyFilter"
                    @last-open-filter="(val) => lastOpenFilter = val"
                    @handle-sort="handleSort"
                    @handler-column-filter-input="handlerColumnFilterInput"
                    @handler-datepicker-range="handlerDatepickerRange"
                    @set-check-apply-filter="(val) => checkApplyFilter = val"
                    @filter="handlerFilter"
                    @handler-radio-btn="handlerRadioBtn"
                    @change-column-filters="(val) => columnFilters = val"
                    @handler-range-value="handlerRangeValue"
                    @change-exclude="changeExclude"
                  />
                </template>
                <template v-slot="scope">
                  <img
                    v-if="field.imgPrepend"
                    :class="field.class"
                    class="ui-table__table-img"
                    :src="field.imgPrepend(scope.row)"
                    alt=""
                  >
                  <img
                    v-if="field.computedImg && field.computedImg(scope.row) !== null && field.computedImgNull"
                    :class="field.class"
                    class="ui-table__table-img"
                    :src="field.computedImg(scope.row)"
                    alt=""
                  >
                  <div
                    v-else-if="field.computedImg && field.computedImg(scope.row) === null && field.computedImgNull"
                    :class="field.class"
                    class="ui-table__table-img t:truncate"
                    v-html="field.computedImgNull(scope.row[field.name], scope.row, field)"
                  />

                  <UiStatusText
                    v-else-if="field.type === 'status'"
                    class="t:px-8"
                    :type="field.status(scope.row[field.name], scope.row, field)"
                    :text="field.statusText(scope.row[field.name], scope.row, field)"
                  />

                  <div
                    v-else-if="field.renderValue"
                    :class="[
                      'ui-table__table-value',
                      field.class,
                      field.computedClass ? field.computedClass(scope.row[field.name], scope.row) : ''
                    ]"
                  >
                    <div
                      class="t:truncate"
                      v-html="field.renderValue(scope.row[field.name], scope.row, field)"
                    />
                  </div>
                  <div
                    v-else
                    v-loading="field.computedLoad && field.computedLoad(scope.row)"
                    :element-loading-text="field.loadLabel"
                    :class="[
                      'ui-table__table-value',
                      {'t:!justify-start': field.linkAppend && field.align !== 'center'},
                      {'ui-table__table-value_copy': isCopyEnabled(field, scope.row)},
                      field.class, field.computedClass ? field.computedClass(scope.row[field.name], scope.row) : ''
                    ]"
                    @click="field.click
                      ? field.click(scope.row, scope.column.columnKey)
                      : isCopyEnabled(field, scope.row)
                        ? copy(scope.row[scope.column.columnKey])
                        : ''"
                  >
                    <div v-if="field.computedLoad && field.computedLoad(scope.row)" />
                    <UiTooltip
                      v-else-if="field.rowTooltip"
                      class="t:flex t:flex-1 t:self-stretch t:min-w-0 t:h-full t:overflow-hidden"
                      placement="top"
                      :tooltip="field.rowTooltip(scope.row)"
                    >
                      <a
                        :href="field.externalLink(scope.row)"
                        target="_blank"
                        class="ui-table__table-link t:flex t:items-center t:w-full t:h-full t:min-w-0 t:overflow-hidden"
                        @click.stop
                      >
                        <span class="t:min-w-0 t:truncate">
                          {{ field.externalLink(scope.row) }}
                        </span>
                      </a>
                    </UiTooltip>
                    <a
                      v-else-if="field.externalLink && field.externalLink(scope.row) !== ''"
                      :href="field.externalLink(scope.row)"
                      target="_blank"
                      class="ui-table__table-link t:truncate"
                      @click.stop
                    >
                      {{ field.externalLink(scope.row) }}
                    </a>
                    <RouterLink
                      v-else-if="field.link && field.link(scope.row) !== '' && field.computedValue"
                      :to="field.link(scope.row)"
                      class="ui-table__table-link t:truncate"
                    >
                      {{ field.computedValue(scope.row, scope.row[field.name]) }}
                    </RouterLink>
                    <RouterLink
                      v-else-if="field.link && field.link(scope.row) !== '' && !field.format"
                      :to="field.link(scope.row)"
                      class="ui-table__table-link t:truncate"
                    >
                      {{ scope.row[field.name] }}
                    </RouterLink>
                    <RouterLink
                      v-else-if="field.link && field.link(scope.row) !== '' && field.format"
                      :to="field.link(scope.row)"
                      class="ui-table__table-link t:truncate"
                    >
                      {{
                        field.format
                          ? formatValue(scope.row[field.name], field.format, field.formatOptions, scope.row)
                          : scope.row[field.name]
                      }}
                    </RouterLink>
                    <!--                    TODO: class="t:truncate" для span удалить если вдруг где то что то поедет-->
                    <span
                      v-else-if="field.computedValue"
                      class="t:truncate"
                      :style="{
                        color: field.controlType === 'icon' && field.controlColor(scope.row)
                      }"
                    >{{ field.computedValue(scope.row, scope.row[field.name]) }}</span>
                    <div
                      v-else-if="field.computedValueHtml"
                      :class="field.class"
                      v-html="field.computedValueHtml(scope.row, scope.row[field.name])"
                    />
                    <div
                      v-else-if="!field.linkAppend"
                      class="t:truncate"
                    >
                      {{ field.format
                        ? formatValue(
                          scope.row[field.name],
                          field.format,
                          field.formatOptions,
                          scope.row,
                          field.tableType,
                          field
                        )
                        : scope.row[field.name] }}
                    </div>

                    <Vnodes
                      v-if="field.linkAppend"
                      :vnodes="field.linkAppend(scope.row)"
                    />
                    <span
                      v-if="field.action && field.controlType === 'link'"
                      @click="field.action(scope.row)"
                    >{{ field.actionName }}</span>

                    <ActionIcon
                      v-if="field.action && field.controlType === 'icon-action'"
                      :tooltip="field.tooltip && $t(field.tooltip)"
                      :color="field.controlColorIco(scope.row)"
                      :is-overlay="field.overlay || true"
                      :icon="typeof field.actionName === 'string' ? field.actionName : field.actionName(scope.row)"
                      @click="field.action(scope.row)"
                    />

                    <UiTooltip
                      v-if="field.action && field.controlType === 'icon'"
                      :tooltip="field.tooltip && $t(field.tooltip)"
                      :disabled="!field.tooltip"
                    >
                      <UiIcon
                        :color="field.controlColorIco(scope.row)"
                        :name="typeof field.actionName === 'string' ? field.actionName : field.actionName(scope.row)"
                        :class="field.iconClass"
                        class="t:ml-4"
                        @click="field.action?.(scope.row)"
                      />
                    </UiTooltip>

                    <UiIcon
                      v-if="isCopyEnabled(field, scope.row)"
                      color="var(--text-primary)"
                      name="clone"
                      class="ui-table__table-value-copy"
                    />
                    <Flag
                      v-if="field.format === 'ip-flag'"
                      :iso="scope.row.country_code"
                      :title="scope.row.country_name"
                    />
                  </div>
                </template>
              </ElTableColumn>
              <ElTableColumn
                v-if="actions"
                :align="actions.align || 'center'"
                :label="actions.name"
                :width="actions.width || 'auto'"
              >
                <template v-slot="scope">
                  <div class="ui-table__table-value ui-table__table-value_actions">
                    <template
                      v-for="action in actions.operations"
                      :key="action.name + '1'"
                    >
                      <template v-if="action.link === 'link'">
                        <RouterLink
                          :key="action.name"
                          :class="action.class"
                          :to="action.callback(scope.row)"
                        >
                          {{ action.name }}
                        </RouterLink>
                      </template>
                      <span
                        v-else-if="action.computedValue"
                        :key="`action-computed-${action.name}`"
                        :class="action.class"
                        v-html="action.computedValue(scope.row)"
                      />
                      <span
                        v-else
                        :key="`action-rest-${action.name}`"
                        :class="action.class"
                        @click="action.callback(scope.row, action.callbackOptions, $event)"
                      >{{ action.name }}</span>
                      <span class="divider" />
                    </template>
                  </div>
                </template>
              </ElTableColumn>
            </slot>
            <slot name="append" />
          </ElTable>
        </template>
      </SortableElTableNew>
    </Transition>
    <LazyLoadingTable
      v-if="isLazyLoading"
    />
  </div>
</template>

<style lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.ui-table{
  //&_fill-parent {
  //  height: 100%;
  //}
  &__table {
    --el-table-border: 1px solid var(--table-header-divider);
    --el-table-row-hover-bg-color: var(--table-row-hover);
    tr:nth-child(even)>td.ui-table__table-cell {
      //background: var(--table-row-highlight);
    }

    th.el-table__cell,
    th.is-leaf.el-table__cell {
      font-size: 12px;
      font-weight: 500;
      padding: 0;
      height: 36px;
      border-bottom: 1px solid var(--table-header-divider);
      border-right: 1px solid var(--table-header-divider);
      background-color: var(--table-header);
      color: var(--table-header-text);
      .cell{
        position: relative;
        line-height: 16px;
        padding: 0 8px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        z-index: 1;
      }
      .sort-caret {
        border: none;
        position: relative;
        width: auto;
        height: 10px;
        color: var(--background-body);
        left: 0;
        &:after {
          content: '';
          font-size: 14px;
          font-family: 'Font Awesome 7 Pro';
          font-weight: 900;
          font-style: normal;
        }
        &.ascending {
          top: -4px;
          &:after {
            content: '\f0d8';
          }
        }
        &.descending {
          bottom: 9px;
          &:after {
            content: '\f0d7';
          }
        }
      }
      &.ascending {
        color: var(--table-header-text-active);
        .sort-caret {
          &.ascending {
            color: var(--table-header-text-active);
          }
        }
      }
      &.descending {
        color: var(--table-header-text-active);
        .sort-caret {
          &.descending {
            color: var(--table-header-text-active);
          }
        }
      }
      &.is-right {
        .cell{
          justify-content: flex-end;
        }
      }
      &.is-center{
        .cell{
          justify-content: center;
        }
      }
      .caret-wrapper {
        visibility: hidden;
      }
      &:hover {
        .caret-wrapper,
        .custom-sorting,
        .custom-header__filters{
          visibility: visible;
        }
      }
      &.ascending,
      &.descending {
        .caret-wrapper,
        .custom-header__filters{
          visibility: visible;
        }
      }
    }
    td.el-table__cell,
    td.el-table__cell.is-leaf {
      border-bottom: 1px solid var(--table-row-divider);
    }
    &.el-table--border td.el-table__cell{
      border-right: 1px solid var(--table-row-divider) !important;
    }

    &.el-table--border .el-table__inner-wrapper:after, .el-table--border:after,
    &.el-table--border:before, .el-table__inner-wrapper:before {
      background-color: var(--table-row-divider);
    }
    .el-table__empty-block {
      min-height: 200px;
      border-right: 1px solid var(--table-row-divider);
    }

    .el-table__footer {
      td.el-table__cell {
        font-size: 12px;
        font-weight: 500;
        padding: 0;

        height: 36px;
        border-top: 1px solid var(--table-header-divider);
        border-bottom: 1px solid var(--table-header-divider);
        border-right: 1px solid var(--table-header-divider) !important;
        background-color: var(--table-header) !important;
        .cell {
          color: var(--table-header-text);
          line-height: 16px;
          padding: 0 8px;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
    }

    &_resize-border-fix {
      &.el-table--fit .el-table__inner-wrapper::before {
        width: auto;
      }

      &.el-table--border .el-table__inner-wrapper::after {
        width: auto;
      }
    }
    &_border {
      tbody {
        .el-table__row:last-child .el-table__cell {
          border-bottom: 1px solid var(--border-divider) !important;
        }
      }
    }
  }
  &__table-header-cell {
    .ui-table__checkbox{
      --el-checkbox-input-border-color-hover: var(--background-body);
      --el-checkbox-checked-input-border-color: var(--background-body);
    }
  }

  &__table-row {
    &:nth-child(2n) {
      background: var(--table-row-highlight);
    }

    &.current-row td.ui-table__table-cell {
      background-color: var(--table-row-selected) !important;
    }
  }

  td.el-table-fixed-column--left,
  td.el-table-fixed-column--right {
    background-color: inherit !important;

    &:after {
      content: '';
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      pointer-events: none;
      transition: background-color .25s ease;
    }
  }

  .el-table .el-table__row:hover {
    td {
      background-color: var(--table-row-hover-cohort) !important;
    }

    td.el-table-fixed-column--left,
    td.el-table-fixed-column--right {
      background-color: var(--table-row-hover-cohort) !important;
    }
  }

  &__table-cell {
    &.el-table__cell {
      font-size: 12px;
      height: 34px;
      padding: 0;
    }
    .cell {
      display: flex;
      line-height: 15px;
      color: var(--text-body);
      padding: 0 8px;
      min-width: 20px;
      height: 100%;
      align-items: center;
      &.el-tooltip {
        min-width: 20px;
      }
    }
    &.is-right{
      .cell{
        justify-content: flex-end;
        .ui-table__table-value{
          display: flex;
          justify-content: flex-end;
        }
      }
    }
    &.is-center{
      .cell{
        justify-content: center;
        .ui-table__table-value  {
          display: flex;
          justify-content: center;
        }
      }
    }
  }
  &__table-value {
    width: 100%;
    min-height: 15px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;

    justify-content: space-between;
    align-items: center;
    height: 100%;
    &_no-child {
      display: block;
    }
    &_actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    &_copy {
      cursor: pointer;
      &:hover {
        .ui-table__table-value-copy {
          display: block;
        }
      }
    }
    &.negative {
      color: var(--text-negative);
    }
    &.positive {
      color: var(--text-positive);
    }
  }
  &__table-value-copy {
    display: none;
  }
  &__append {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__table-img {
    max-height: 25px;
    padding: 0 8px;
  }
  &__table-empty {
    color: var(--text-body-tertiary);
    text-align: center;
    line-height: 1;
  }
  &__table-empty-text {
    font-size: 16px;
    font-weight: 400;
  }
  &__table-link {
    color: var(--text-link);
    font-size: 12px;
  }
  &__checkbox {
    --el-checkbox-border-radius: 4px;
    --el-checkbox-bg-color: #fff;
    --el-checkbox-input-border: var(--border-default);
    --el-checkbox-input-border: 1px solid var(--el-border-color);;
    --el-checkbox-input-border-color-hover: var(--el-border-color);
  }
}
</style>
